<template>
  <div @click="emit('updateOnboardingStep', { value: props.currentStep - 1 })" class="cursor-pointer hover:scale-105 duration-300 rounded-md bg-gray-800 px-3 py-2.5 text-text-primary flex justify-center items-center text-xl absolute top-8 left-4 lg:left-16">
    <img class="w-4 mr-1" src="../../assets/icons/leftarrow.svg">
    <h1 class="text-xs text-text-primary">{{ $t('common.back') }}</h1>
  </div>
  <div @click="emit('updateOnboardingStep', { value: props.currentStep + 1 })" class="cursor-pointer hover:scale-105 duration-300 rounded-md bg-gray-800 px-3 py-2.5 text-text-primary flex justify-center items-center text-xl absolute top-8 right-4 lg:right-16">
    <h1 class="text-xs text-text-primary">{{ $t('common.skip') }}</h1>
    <img class="w-4 ml-1" src="../../assets/icons/rightarrow.svg">
  </div>
  <div class="flex flex-col h-full items-center lg:justify-center w-full mt-16 lg:mt-0 lg:w-[414px]">
    <h1 class="text-text-primary text-[36px] font-bold pb-3">{{ $t('onboarding.steps.bot_server_setup') }}</h1>
    <h2 class="text-text-secondary text-center pb-6">{{ $t('onboarding.steam_bot.subtitle') }}</h2>
    <div class="flex flex-col gap-6 w-full">
      <div class="w-full flex flex-col rounded-md border border-gray-800 p-6 gap-6">
        <div class="flex w-full items-center">
          <h1 class="text-text-primary text-xl font-medium flex-1">{{ $t('onboarding.steam_bot.add_bot') }}</h1>
        </div>
        <div class="flex flex-col gap-6">
          <div class="flex flex-col gap-4 text-text-secondary text-sm">
            <h1>{{ $t('onboarding.steam_bot.notify_intro') }}</h1>
            <h1 class="flex gap-2 items-center"><CheckIcon class="h-5 w-5 text-primary-500" />{{ $t('onboarding.steam_bot.notify_server_details') }}</h1>
            <h1 class="flex gap-2 items-center"><CheckIcon class="h-5 w-5 text-primary-500" />{{ $t('onboarding.steam_bot.notify_highlights') }}</h1>
            <h1 class="flex gap-2 items-center"><CheckIcon class="h-5 w-5 text-primary-500" />{{ $t('onboarding.steam_bot.notify_match_details') }}</h1>
          </div>

          <a
            v-if="!hasBotAdded"
            :href="friendLink"
            target="_blank"
            class="cursor-pointer w-full py-2.5 text-text-primary bg-primary-600 hover:bg-primary-500 rounded-md flex items-center justify-center text-xs gap-2"
          >
            <img class="h-4" src="../../assets/icons/steam.svg" :alt="$t('common.continue')" />
            {{ $t('onboarding.steam_bot.add_friend') }}
          </a>
          <div v-else class="w-full py-2.5 text-text-secondary bg-primary-600/30 rounded-md flex items-center justify-center text-xs gap-2">
            <HandThumbUpIcon class="w-4 h-4 text-text-secondary" />
            {{ $t('onboarding.steam_bot.added_friend') }}
          </div>
        </div>
      </div>
      <div class="w-full flex flex-col rounded-md border border-gray-800 p-6 gap-6">
        <div class="flex w-full items-center">
          <h1 class="text-text-primary text-xl font-medium flex-1">{{ $t('onboarding.steam_bot.server_setup') }}</h1>
        </div>
        <div class="flex flex-col gap-6">
          <div class="flex gap-10 items-center">
            <h1 class="text-text-primary text-sm whitespace-nowrap w-20">{{ $t('onboarding.steam_bot.location') }}</h1>

            <Menu as="div" class="relative inline-block text-left w-full">
              <MenuButton
                class="relative w-full disabled:bg-gray-800 disabled:text-text-disabled inline-flex h-8 items-center px-2 py-2 border border-gray-700 shadow-sm text-sm font-medium rounded-md text-text-primary bg-gray-800 hover:bg-gray-700"
              >
                <div class="flex-1 text-left flex items-center gap-2">
                  <flag
                    :rounded="true"
                    :iso="activeLocation?.flag"
                    class="rounded-full h-4 w-4"
                  />
                  {{ activeLocation?.name }}
                  <span class="text-text-disabled">•</span>
                  {{ latencyLabelFor(activeLocation?.value) }}
                </div>
                <ChevronDownIcon class="-mr-1 ml-4 h-5 w-5" aria-hidden="true" />
              </MenuButton>

              <transition
                enter-active-class="transition ease-out duration-100"
                enter-from-class="transform opacity-0 scale-95"
                enter-to-class="transform opacity-100 scale-100"
                leave-active-class="transition ease-in duration-75"
                leave-from-class="transform opacity-100 scale-100"
                leave-to-class="transform opacity-0 scale-95"
              >
                <MenuItems
                  class="absolute right-0 z-50 bottom-full mb-2 w-full max-h-[320px] overflow-y-auto origin-bottom-right divide-y divide-gray-100 rounded-md bg-gray-800 border border-gray-700 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                >
                  <div class="py-1 flex flex-col px-4">
                    <MenuItem
                      :key="location"
                      v-for="location in locations"
                      v-slot="{ active }"
                      @click="selectLocation(location)"
                    >
                      <div
                        :class="[
                          active ? 'text-text-primary' : 'text-text-secondary',
                          'group flex items-center gap-2 py-2 text-sm w-full justify-start cursor-pointer',
                        ]"
                      >
                        <flag
                          :rounded="true"
                          :iso="location.flag"
                          class="rounded-full h-4 w-4"
                        />
                        <span class="flex-1">{{ location?.name }}</span>
                        <span class="text-text-secondary">{{ latencyLabel(location) }}</span>
                        <CheckIcon v-if="location.name === activeLocation?.name" class="h-5 w-5 ml-2 text-primary-400" />
                      </div>
                    </MenuItem>
                  </div>
                </MenuItems>
              </transition>
            </Menu>
          </div>

          <div class="flex gap-10 items-center">
            <h1 class="text-text-primary text-sm whitespace-nowrap w-20">{{ $t('onboarding.steam_bot.language') }}</h1>

            <Menu as="div" class="relative inline-block text-left w-full">
              <MenuButton
                class="relative w-full disabled:bg-gray-800 disabled:text-text-disabled inline-flex h-8 items-center px-2 py-2 border border-gray-700 shadow-sm text-sm font-medium rounded-md text-text-primary bg-gray-800 hover:bg-gray-700"
              >
                <div class="flex-1 text-left">
                  {{ selectedLanguage.name }}
                </div>
                <ChevronDownIcon class="-mr-1 ml-4 h-5 w-5" aria-hidden="true" />
              </MenuButton>

              <transition
                enter-active-class="transition ease-out duration-100"
                enter-from-class="transform opacity-0 scale-95"
                enter-to-class="transform opacity-100 scale-100"
                leave-active-class="transition ease-in duration-75"
                leave-from-class="transform opacity-100 scale-100"
                leave-to-class="transform opacity-0 scale-95"
              >
                <MenuItems
                  class="absolute right-0 z-50 bottom-full mb-2 w-full max-h-[320px] overflow-y-auto origin-bottom-right divide-y divide-gray-100 rounded-md bg-gray-800 border border-gray-700 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                >
                  <div class="py-1 flex flex-col px-4">
                    <MenuItem
                      :key="language"
                      v-for="language in languages"
                      v-slot="{ active }"
                      @click="selectLanguage(language)"
                    >
                      <div
                        :class="[
                          active ? 'text-text-primary' : 'text-text-secondary',
                          'group flex items-center py-2 text-sm w-full justify-start cursor-pointer',
                        ]"
                      >
                        {{ language.name }}
                        <CheckIcon v-if="language.name === selectedLanguage?.name" class="h-5 w-5 ml-auto text-primary-400" />
                      </div>
                    </MenuItem>
                  </div>
                </MenuItems>
              </transition>
            </Menu>
          </div>
        </div>
      </div>
    </div>
    <button :disabled="!hasBotAdded" @click="continueAndSubmit()" class="cursor-pointer group w-full py-3 text-text-primary disabled:text-text-primary/30 bg-primary-600 disabled:bg-primary-600/30 hover:bg-primary-500 mt-10 rounded-md flex items-center justify-center gap-1">
      {{ $t('common.continue') }}
      <img class="h-4 brightness-200 group-disabled:brightness-50" src="../../assets/icons/rightarrow.svg" :alt="$t('common.continue')" />
    </button>
  </div>
  <AlertPopup />
</template>

<script setup>
import {
  onMounted, ref, getCurrentInstance, onBeforeUnmount, computed, Transition,
} from 'vue';
import { useAuth } from '@websanova/vue-auth/src/v3';
import axios from 'axios';
import { CheckIcon, HandThumbUpIcon, ChevronDownIcon } from '@heroicons/vue/20/solid';
import {
  Menu, MenuButton, MenuItem, MenuItems,
} from '@headlessui/vue';
import { useRoute, useRouter } from 'vue-router';
import {
  latencyLabel, latencyLabelFor, loadServerLocations, serverLocations, sortByLatency,
} from '../../utils/server_locations.js';
import { loadUserPreferences, resolveServerLocation, setServerLocation } from '../../utils/user_preferences.js';
import Pill from '../Pill.vue';
import AlertPopup from '../AlertPopup.vue';

const auth = useAuth();
const user = ref(auth.user());
const matchToken = ref('');
const authenticationCode = ref('');
const emit = defineEmits(['updateOnboardingStep']);
const route = useRoute();
const router = useRouter();
const friendLink = ref('');
const websocket = ref(null);
const subscription = ref(null);
const instance = getCurrentInstance();
const hasBotAdded = ref(user.value.has_steam_bot_added);
const languages = [
  { name: 'English', code: 'en' },
  { name: 'Português', code: 'pt_t' },
  { name: 'Danish', code: 'dk' },
  { name: 'Chinese', code: 'cn' },
  { name: 'Deutsch', code: 'de' },
  { name: 'Polish', code: 'pl' },
];
const selectedLanguage = ref(languages[0]);
const localSelectedLocation = ref(null);

const props = defineProps({
  currentStep: Number,
});

if (user.value.language) {
  selectedLanguage.value = languages.find((language) => language.code === user.value.language);
}

const locations = computed(() => sortByLatency(serverLocations.value));

const activeLocation = computed(() => localSelectedLocation.value || resolveServerLocation(locations.value));

function getLocations() {
  return Promise.all([loadServerLocations(), loadUserPreferences()]);
}

function selectLocation(location) {
  setServerLocation(location);
  localSelectedLocation.value = location;
}

function getBot() {
  axios({
    method: 'get',
    url: '/steam_bots/bot',
  }).then((response) => {
    friendLink.value = response.data.data.invite_link;
  });
}

// Only save a language the user picked, so an untouched English default stays unset.
const languagePicked = ref(false);

function selectLanguage(language) {
  selectedLanguage.value = language;
  languagePicked.value = true;
}

function subscribeToSteamBot() {
  const cable = instance.appContext.config.globalProperties.$cable;

  if (!websocket.value) {
    websocket.value = cable.connect();
  }
  if (!subscription.value) {
    subscription.value = websocket.value.subscriptions.create(
      { channel: 'SteamBotChannel' },
      {
        received: (data) => {
          if (data.event === 'steam_bot_added') {
            hasBotAdded.value = true;
          }
        },
      },
    );
  }
}

function continueAndSubmit() {
  emit('updateOnboardingStep', { value: props.currentStep + 1 });
  cleanupWebsocket();
  if (matchToken.value) {
    axios({
      method: 'post',
      url: '/matches',
      params: {
        match: {
          match_platform: 'valve',
          match_token: matchToken.value,
        },
      },
    });
  }
  if (languagePicked.value) {
    axios({
      method: 'put',
      url: '/users/update_user',
      data: {
        language: selectedLanguage.value.code,
      },
    }).then(() => {
      user.value.language = selectedLanguage.value.code;
    });
  }
}

function cleanupWebsocket() {
  if (subscription.value) {
    subscription.value.unsubscribe();
    subscription.value = null;
  }
  if (websocket.value) {
    websocket.value.disconnect();
    websocket.value = null;
  }
}

onMounted(() => {
  getBot();
  subscribeToSteamBot();
  getLocations();
});

onBeforeUnmount(() => {
  cleanupWebsocket();
});

</script>
